import type { EditScannerConfigInput } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  validateIpAddresses,
  type IpValidationResults,
} from '@packages/utils/validate-ip-addresses/validate-ip-addresses';

type ValidateIpEntries = (
  ipAddresses: string[] | undefined,
  context: Yup.TestContext<EditScannerConfigInput>,
) => boolean | Yup.ValidationError;

export const useScannerConfigValidationSchema = () => {
  const { t } = useTranslation('telemetryScanSchedules', {
    keyPrefix: 'targetValidation',
  });

  const validateIpEntries: ValidateIpEntries = (
    ipAddresses,
    { createError, path },
  ) => {
    const results: IpValidationResults = validateIpAddresses(ipAddresses || []);

    if (!results) return true;

    if (results.invalid.size) {
      return createError({
        message: t('invalidIp', {
          target: [...results.invalid].join(', '),
        }),
        path,
      });
    }

    if (Object.keys(results.duplicate).length) {
      const allDuplicates = Object.values(results.duplicate).reduce(
        (previousValue: string[], currentValue: Set<string>) =>
          previousValue.concat([...currentValue]),
        [],
      );

      return createError({
        message: t('duplicateIps', {
          targets: allDuplicates.join(', '),
        }),
        path,
      });
    }

    return true;
  };

  return Yup.object()
    .shape({
      bruteScan: Yup.boolean(),
      cgiScan: Yup.boolean(),
      hostIden: Yup.boolean(),
      hostIdenServers: Yup.array(Yup.string().required()).test(
        'host-dns-validation',
        '', // dynamically set to list invalid targets when they exist
        validateIpEntries,
      ),
      pingOnly: Yup.boolean(),
      scanExclusionList: Yup.array(Yup.string().required()).test(
        'scan-exclusion-validation',
        '', // dynamically set to list invalid targets when they exist
        validateIpEntries,
      ),
      vulnScan: Yup.boolean(),
    })
    .required();
};
